/**
 * Where to send a patient after they sign in, when they were part-way through
 * something before being asked to log in.
 *
 * This exists for pre-orders: that traffic arrives cold from a social post, so
 * the visitor meets the login screen in the middle of the flow. `PrivateRoute`
 * redirects to "/" and keeps nothing, so without this they would land on the
 * dashboard having lost the product, the payment plan and the campaign they
 * came from.
 *
 * Only in-app paths are ever honoured, so a crafted link cannot use this to
 * bounce someone to another site after login.
 */

const KEY = 'emed4u.returnTo';

const isSafePath = (path) => typeof path === 'string'
  && path.startsWith('/')
  && !path.startsWith('//')          // "//evil.com" is a protocol-relative URL
  && !path.includes('://');

// Long enough to sign up and verify an email, short enough that an abandoned
// attempt cannot still be steering a different login an hour later.
const MAX_AGE_MS = 60 * 60 * 1000;

export const setReturnTo = (path) => {
  try {
    if (isSafePath(path)) sessionStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));
  } catch (e) { /* private mode: the flow still works, it just ends on the dashboard */ }
};

/** Reads the stored value, discarding it once it is too old to act on. */
const readFresh = () => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved || !saved.at || Date.now() - saved.at > MAX_AGE_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return isSafePath(saved.path) ? saved.path : null;
  } catch (e) {
    // Anything unparseable is from an older build; drop it rather than guess.
    try { sessionStorage.removeItem(KEY); } catch (err) { /* nothing to clear */ }
    return null;
  }
};

/** Reads and clears it, so it is only ever used once. */
export const takeReturnTo = () => {
  const path = readFresh();
  try { sessionStorage.removeItem(KEY); } catch (e) { /* nothing to clear */ }
  return path;
};
